import {useCallback, useContext, useEffect} from 'react';
import {channel} from "redux-saga";
import {call, getContext, put, setContext, take} from 'redux-saga/effects';

import CancelTokenSource from "../utils/cancel";
import actionFactories from './actions';
import {putFeedback, withFeedbackChannel} from './feedback';
import {AppContext, useServerEffect} from './index';

export const actions = {};
Object.keys(actionFactories).forEach((key) => {
    const actionFactory = actionFactories[key];
    actions[key] = (...args) => put({type: key, payload: actionFactory(...args)});
});

const useSagaRunner = (feedbackCh) => {
    const {sagaMiddleware, readyCh} = useContext(AppContext);
    return useCallback((token, saga, ...args) => {
        const taskCh = channel();
        const task = sagaMiddleware.run(function* () {
            const task = yield take(taskCh);
            yield setContext({readyCh});
            return yield call(withFeedbackChannel, feedbackCh, function* () {
                yield putFeedback({error: null});
                try {
                    return yield call([task, saga], ...args);
                } catch (error) {
                    yield putFeedback({error});
                    throw error;
                }
            });
        });
        token?.register(() => task.cancel());
        taskCh.put(task);
        taskCh.close();
        return task;
    }, [sagaMiddleware, feedbackCh, readyCh]);
};

export function* bypassingReadyChecks(saga, ...args) {
    yield setContext({ready: true});
    yield call(saga, ...args);
}

export function* signalReady() {
    const readyCh = yield getContext('readyCh')
    readyCh.close();
}

export function* waitUntilReady() {
    const bypassed = yield getContext('ready');
    if (!bypassed) {
        const readyCh = yield getContext('readyCh');
        yield take(readyCh);
    }
}

export const useSagaEffect = (feedbackCh, saga) => {
    const run = useSagaRunner(feedbackCh);
    useEffect(() => {
        const source = new CancelTokenSource();
        run(source.token, saga).toPromise().catch(() => {
            // silently ignored, use error feedback
        });
        return () => source.cancel();
    }, [run, saga]);
};

export const useIsomorphicSagaEffect = (feedbackCh, saga) => {
    useServerEffect(saga);
    const run = useSagaRunner(feedbackCh);
    useEffect(() => {
        const source = new CancelTokenSource();
        run(source.token, saga).toPromise().catch(() => {
            // silently ignored, use error feedback
        });
        return () => source.cancel();
    }, [run, saga]);
};

export const useSagaCallback = (feedbackCh, saga, cancelToken=null) => {
    const run = useSagaRunner(feedbackCh);
    return useCallback(
        (...args) => run(cancelToken, saga, ...args).toPromise(),
        [run, cancelToken, saga]
    );
};
